import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../supabase';
import './ShareProtocolModal.css';
import './PostCreationBox.css';
import '../styles/buttons.css';
import '../styles/color-scheme.css';
import { Icon } from './Icon.js';
import AttachmentDisplay from './AttachmentDisplay.js';
import { useUserProfile } from '../hooks/useUserProfile.js';
import { useAttachments } from '../hooks/useAttachments.js';
import { useImageUpload } from '../hooks/useImageUpload.js';
import {
  validatePostData,
  createPost,
  processAttachment,
  prepareAttachmentsForSubmission,
  createBiohackTags,
  createBiomarkerTags,
  getBiohacksFromProtocol
} from '../utils/postSubmission.js';
import SearchService from '../services/SearchService.js';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';

const ShareProtocolModal = ({ protocol, onClose, onPostCreated }) => {
  // Core form state
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded since protocol is already attached
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const fileInputRef = useRef(null);
  const postCreationRef = useRef(null);
  const textareaRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const searchTimerRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const biomarkerSearchTimerRef = useRef(null);

  // Custom hooks
  const user = useUserProfile();
  const {
    attachments,
    addImages,
    addProtocol,
    addBiohacks,
    removeAttachment,
    resetAttachments
  } = useAttachments();
  
  const { isUploading, error: uploadError, uploadImages, clearError: clearUploadError } = useImageUpload(addImages);
  
  // Biohacks from attached protocol
  const [protocolBiohacks, setProtocolBiohacks] = useState([]);
  // Biohack suggestions state
  const [biohackSuggestions, setBiohackSuggestions] = useState([]);
  // Biomarker tags state (separate from biomarker attachment)
  const [biomarkerTags, setBiomarkerTags] = useState([]);
  const [biomarkerSuggestions, setBiomarkerSuggestions] = useState([]);

  // Pre-attach the protocol when component mounts
  useEffect(() => {
    if (protocol && !attachments.protocol) {
      addProtocol(protocol);
    }
  }, [protocol, attachments.protocol, addProtocol]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.classList.add('share-protocol-modal-open');
    return () => {
      document.body.classList.remove('share-protocol-modal-open');
    };
  }, []);

  // Extract biohacks from attached protocol
  useEffect(() => {
    const extractProtocolBiohacks = async () => {
      if (attachments.protocol) {
        const biohacks = await getBiohacksFromProtocol(attachments.protocol);
        setProtocolBiohacks(biohacks || []);
      } else {
        setProtocolBiohacks([]);
      }
    };

    extractProtocolBiohacks();
  }, [attachments.protocol]);

  // Search for biohack suggestions as user types
  useEffect(() => {
    if (!isExpanded || !content.trim()) {
      setBiohackSuggestions([]);
      return;
    }

    // Don't show suggestions if content ends with a space (user just finished a word)
    if (content.endsWith(' ')) {
      setBiohackSuggestions([]);
      return;
    }

    // Get the last word being typed
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1]?.toLowerCase().trim() || '';

    // Need at least 2 characters to search
    if (lastWord.length < 2) {
      setBiohackSuggestions([]);
      return;
    }

    // Debounce the search
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await SearchService.searchBiohacksByName(lastWord, 5);
        
        // Filter out already selected biohacks
        const selectedIds = new Set((attachments.biohacks || []).map(b => b.id));
        const protocolIds = new Set((protocolBiohacks || []).map(b => b.id));
        const allSelectedIds = new Set([...selectedIds, ...protocolIds]);
        const filteredResults = (results || []).filter(b => !allSelectedIds.has(b.id));
        
        setBiohackSuggestions(filteredResults);
        // Clear biomarker suggestions when biohack suggestions are set (biohack takes priority)
        if (filteredResults.length > 0) {
          setBiomarkerSuggestions([]);
        }
      } catch (error) {
        console.error('Error searching biohacks:', error);
        setBiohackSuggestions([]);
      }
    }, 300);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [content, isExpanded, attachments.biohacks, protocolBiohacks]);

  // Search for biomarker suggestions as user types
  useEffect(() => {
    if (!isExpanded || !content.trim()) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Don't show suggestions if content ends with a space (user just finished a word)
    if (content.endsWith(' ')) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Get the last word being typed
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1]?.toLowerCase().trim() || '';

    // Need at least 2 characters to search
    if (lastWord.length < 2) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Only show biomarker suggestions if there are no biohack suggestions (biohack takes priority)
    if (biohackSuggestions.length > 0) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Debounce the search
    if (biomarkerSearchTimerRef.current) {
      clearTimeout(biomarkerSearchTimerRef.current);
    }

    biomarkerSearchTimerRef.current = setTimeout(async () => {
      try {
        const results = await SearchService.searchBiomarkersByName(lastWord, 5);
        
        // Filter out already selected biomarkers
        const selectedIds = new Set(biomarkerTags.map(b => b.id));
        const filteredResults = (results || []).filter(b => !selectedIds.has(b.id));
        
        // Set biomarker suggestions (they will be cleared if biohack suggestions exist due to the check above)
        setBiomarkerSuggestions(filteredResults);
      } catch (error) {
        console.error('Error searching biomarkers:', error);
        setBiomarkerSuggestions([]);
      }
    }, 350); // Slightly longer delay than biohack (300ms) to ensure biohack completes first

    return () => {
      if (biomarkerSearchTimerRef.current) {
        clearTimeout(biomarkerSearchTimerRef.current);
      }
    };
  }, [content, isExpanded, biomarkerTags, biohackSuggestions]);

  // Remove biotags when their text is deleted from content
  useEffect(() => {
    if (!content || !isExpanded) return;

    const contentLower = content.toLowerCase();
    // Split content into words, removing punctuation for matching
    const words = contentLower.split(/\s+/).map(word => word.replace(/[.,;:!?()[\]{}"'`]/g, ''));

    // Check manually created biohack tags (not protocol biohacks)
    if (attachments.biohacks && attachments.biohacks.length > 0) {
      const biohacksToKeep = attachments.biohacks.filter(biohack => {
        const biohackNameLower = biohack.name.toLowerCase();
        // Check if the biohack name appears as a complete word/phrase in the content
        // Split the biohack name into words for multi-word matching
        const biohackWords = biohackNameLower.split(/\s+/);
        
        // For single-word biohacks, check exact word match
        if (biohackWords.length === 1) {
          return words.includes(biohackWords[0]);
        }
        
        // For multi-word biohacks, check if all words appear consecutively in content
        // This handles cases like "Vitamin D" where we want to match the full phrase
        const biohackPhrase = biohackWords.join(' ');
        return contentLower.includes(biohackPhrase);
      });

      // Only update if there's a difference to avoid unnecessary re-renders
      if (biohacksToKeep.length !== attachments.biohacks.length) {
        addBiohacks(biohacksToKeep);
      }
    }

    // Check biomarker tags
    // Don't remove biomarker tags that come from the biomarker attachment
    if (biomarkerTags && biomarkerTags.length > 0) {
      const biomarkersToKeep = biomarkerTags.filter(biomarker => {
        // If this biomarker comes from the biomarker attachment, always keep it
        if (attachments.biomarker && attachments.biomarker.id === biomarker.id) {
          return true;
        }
        
        // For manually typed biomarker tags, check if the name is still in content
        const biomarkerNameLower = biomarker.name.toLowerCase();
        // Split the biomarker name into words for multi-word matching
        const biomarkerWords = biomarkerNameLower.split(/\s+/);
        
        // For single-word biomarkers, check exact word match
        if (biomarkerWords.length === 1) {
          return words.includes(biomarkerWords[0]);
        }
        
        // For multi-word biomarkers, check if all words appear consecutively in content
        const biomarkerPhrase = biomarkerWords.join(' ');
        return contentLower.includes(biomarkerPhrase);
      });

      // Only update if there's a difference to avoid unnecessary re-renders
      if (biomarkersToKeep.length !== biomarkerTags.length) {
        setBiomarkerTags(biomarkersToKeep);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, isExpanded]);

  // Add biohack tag when suggestion is clicked or Tab is pressed
  const addBiohackTag = (biohack) => {
    // Check if already tagged (either manually or from protocol)
    const alreadyTaggedManually = (attachments.biohacks || []).some(b => b.id === biohack.id);
    const alreadyTaggedFromProtocol = (protocolBiohacks || []).some(b => b.id === biohack.id);
    if (alreadyTaggedManually || alreadyTaggedFromProtocol) {
      setBiohackSuggestions([]);
      return;
    }

    // Replace the last word in content with the full biohack name
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1] || '';
    
    // Only replace if there's actually a word being typed
    if (lastWord) {
      // Remove the last word and add the biohack name
      const newWords = words.slice(0, -1);
      newWords.push(biohack.name);
      setContent(newWords.join(' ') + ' ');
    }

    // Add to biohack attachments
    addBiohacks([...(attachments.biohacks || []), biohack]);
    
    // Clear suggestions
    setBiohackSuggestions([]);
    
    // Focus back on the input
    if (textareaRef.current) {
      setTimeout(() => textareaRef.current.focus(), 0);
    }
  };

  // Add biomarker tag when suggestion is clicked or Tab is pressed
  const addBiomarkerTag = (biomarker) => {
    // Check if already tagged (either manually or from biomarker attachment)
    const alreadyTaggedManually = biomarkerTags.some(b => b.id === biomarker.id);
    const alreadyTaggedFromAttachment = attachments.biomarker && attachments.biomarker.id === biomarker.id;
    if (alreadyTaggedManually || alreadyTaggedFromAttachment) {
      setBiomarkerSuggestions([]);
      return;
    }

    // Replace the last word in content with the full biomarker name
    const words = content.trim().split(/\s+/);
    const lastWord = words[words.length - 1] || '';
    
    // Only replace if there's actually a word being typed
    if (lastWord) {
      // Remove the last word and add the biomarker name
      const newWords = words.slice(0, -1);
      newWords.push(biomarker.name);
      setContent(newWords.join(' ') + ' ');
    }

    // Add to biomarker tags
    setBiomarkerTags([...biomarkerTags, biomarker]);
    
    // Clear suggestions
    setBiomarkerSuggestions([]);
    
    // Focus back on the input
    if (textareaRef.current) {
      setTimeout(() => textareaRef.current.focus(), 0);
    }
  };

  // Check if we have any attachments
  const hasAttachments = attachments.images.length > 0 || 
                        attachments.protocol || 
                        attachments.biomarker ||
                        attachments.protocolReport;

  // Consolidated error handling
  const displayError = error || uploadError;

  // Handle content changes
  const handleContentChange = (e) => {
    const value = e.target.value;
    setContent(value);
    
    // Clear errors when user types
    if (displayError) {
      setError('');
      clearUploadError();
    }
  };
  
  // Handle keyboard events for suggestion completion
  const handleKeyDown = (e) => {
    // Tab key to accept first suggestion (prioritize biohack over biomarker)
    if (e.key === 'Tab' && biohackSuggestions.length > 0) {
      e.preventDefault();
      addBiohackTag(biohackSuggestions[0]);
    } else if (e.key === 'Tab' && biomarkerSuggestions.length > 0) {
      e.preventDefault();
      addBiomarkerTag(biomarkerSuggestions[0]);
    }
    
    // Escape to clear suggestions
    if (e.key === 'Escape') {
      setBiohackSuggestions([]);
      setBiomarkerSuggestions([]);
    }
  };
  
  // Handle title changes
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    if (displayError) {
      setError('');
      clearUploadError();
    }
  };

  // Handle image selection
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // If a protocol report is selected, don't allow adding images
    if (attachments.protocolReport) {
      setError('Remove the report to add images');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    await uploadImages(files, attachments.images.length);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle post submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      validatePostData(content, title);
    } catch (validationError) {
      setError(validationError.message);
      isSubmittingRef.current = false;
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('You must be signed in to post.');

      // Check user exists in user_profiles table
      const { data: userRow, error: userRowError } = await supabase
        .from('user_profiles')
        .select('user_id')
        .eq('user_id', authUser.id)
        .maybeSingle();
        
      if (userRowError || !userRow) {
        throw new Error('User not found. Please sign out and sign in again.');
      }

      // Create post
      const postData = {
        user_id: authUser.id,
        content: content.trim(),
        title: title.trim() || null,
        visibility: 'followers',
        has_attachment: attachments.images.length > 0 || 
                       !!attachments.protocol || 
                       attachments.biohacks.length > 0 ||
                       !!attachments.biomarker ||
                       !!attachments.protocolReport
      };

      const postId = await createPost(postData);

      // Handle attachments
      const allAttachmentsForSubmission = prepareAttachmentsForSubmission(attachments);
      
      // Process attachments
      for (const attachment of allAttachmentsForSubmission) {
        await processAttachment(postId, attachment);
      }

      // Collect all biohacks for tagging
      const allBiohacks = [];

      // Add manually entered biohacks
      if (attachments.biohacks && attachments.biohacks.length > 0) {
        allBiohacks.push(...attachments.biohacks);
      }

      // Add biohacks from attached protocol
      if (protocolBiohacks && protocolBiohacks.length > 0) {
        allBiohacks.push(...protocolBiohacks);
      }

      // Create biohack tags for all collected biohacks
      if (allBiohacks.length > 0) {
        await createBiohackTags(postId, allBiohacks);
      }

      // Collect all biomarkers for tagging
      const allBiomarkers = [];

      // Add manually entered biomarker tags
      if (biomarkerTags && biomarkerTags.length > 0) {
        allBiomarkers.push(...biomarkerTags);
      }

      // Create biomarker tags for all collected biomarkers
      if (allBiomarkers.length > 0) {
        await createBiomarkerTags(postId, allBiomarkers);
      }

      // Reset form
      setContent('');
      setTitle('');
      resetAttachments();
      setBiomarkerTags([]);
      setProtocolBiohacks([]);
      setIsExpanded(false);
      
      if (onPostCreated) onPostCreated();
      if (onClose) onClose();

    } catch (error) {
      console.error('Post creation error:', error);
      setError(getUserFacingErrorMessage(error, 'Failed to post. Please try again.'));
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const currentLoading = isLoading || isUploading;

  return (
    <div className="share-protocol-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="share-protocol-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-protocol-modal-header">
          <h2>Congrats for making a new protocol. Share it with the community!</h2>
          <button className="share-protocol-modal-close" onClick={onClose}>
            <Icon name="X" className="icon--sm" />
          </button>
        </div>
        
        <div className="share-protocol-modal-divider"></div>
        
        <div className="share-protocol-modal-content">
          <div className={`post-creation-box ${isExpanded ? 'is-expanded' : ''}`} ref={postCreationRef}>
            <form onSubmit={handleSubmit}>
              <div className="post-create-card">
                <div className="post-create-header">
                  <div className="post-create-avatar">
                    {user.profilePictureUrl ? (
                      <img src={user.profilePictureUrl} alt={`${user.username}'s profile`} />
                    ) : user.username ? (
                      <div className="avatar-fallback">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                    ) : (
                      <div className="avatar-loading">...</div>
                    )}
                  </div>
                  
                  <div className="post-create-main">
                    <div className="post-create-expanded">
                      <input
                        className="post-create-title"
                        placeholder={`What did you discover today${user.username ? ' ' + user.username : ''}?`}
                        value={title}
                        onChange={handleTitleChange}
                        disabled={currentLoading}
                        maxLength={100}
                      />
                      <div className={`post-create-input-wrapper ${hasAttachments ? 'has-attachments' : ''}`} ref={inputWrapperRef}>
                        <textarea
                          className="post-create-content"
                          placeholder="Add detail (optional)"
                          value={content}
                          onChange={handleContentChange}
                          onKeyDown={handleKeyDown}
                          ref={textareaRef}
                          disabled={currentLoading}
                          maxLength={500}
                          rows={4}
                          autoFocus
                        />
                        
                        {/* Attachments Preview Inside Extended Input Box */}
                        {hasAttachments && (
                          <>
                            <div className="content-attachments-divider"></div>
                            <div className="content-attachments-section">
                              <AttachmentDisplay
                                attachments={attachments}
                                onRemove={(type, id) => {
                                  // If removing biomarker attachment, also remove from biomarkerTags
                                  if (type === 'biomarker' && attachments.biomarker) {
                                    setBiomarkerTags(biomarkerTags.filter(b => b.id !== attachments.biomarker.id));
                                  }
                                  removeAttachment(type, id);
                                }}
                                mode="preview"
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Biotag Suggestion Prompt - Shows biohack or biomarker, prioritized */}
                      {(biohackSuggestions.length > 0 || biomarkerSuggestions.length > 0) && (
                        <div className="biohack-suggestion-prompt">
                          <span className="suggestion-prompt-text">Press tab to insert biotag:</span>
                          {biohackSuggestions.length > 0 ? (
                            <button
                              type="button"
                              className="biohack-tag biohack-tag-suggestion"
                              onClick={() => addBiohackTag(biohackSuggestions[0])}
                              title="Click to add this tag"
                            >
                              {biohackSuggestions[0].name}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="biohack-tag biohack-tag--biomarker biohack-tag-suggestion"
                              onClick={() => addBiomarkerTag(biomarkerSuggestions[0])}
                              title="Click to add this tag"
                            >
                              {biomarkerSuggestions[0].name}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Tagged Biotags (Biohacks and Biomarkers) */}
                      {((attachments.biohacks && attachments.biohacks.length > 0) || 
                        (protocolBiohacks && protocolBiohacks.length > 0) ||
                        (biomarkerTags && biomarkerTags.length > 0)) && (
                        <div className="detected-biohacks-tags">
                          {/* Manually entered biohack tags */}
                          {attachments.biohacks && attachments.biohacks.map((biohack) => (
                            <span key={biohack.id} className="biohack-tag">
                              {biohack.name}
                            </span>
                          ))}
                          {/* Biohack tags from attached protocol */}
                          {protocolBiohacks && protocolBiohacks.map((biohack) => (
                            <span key={biohack.id} className="biohack-tag">
                              {biohack.name}
                            </span>
                          ))}
                          {/* Manually entered biomarker tags */}
                          {biomarkerTags && biomarkerTags.map((biomarker) => (
                            <span key={biomarker.id} className="biohack-tag biohack-tag--biomarker">
                              {biomarker.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Error message */}
                    {displayError && <div className="post-create-error">{displayError}</div>}
                    
                    {/* Actions */}
                    <div className="post-create-actions">
                      <div className="post-create-filters">
                        <button 
                          type="button"
                          className="filter-btn"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={currentLoading || !!attachments.protocolReport}
                          title={attachments.protocolReport ? 'Remove report first to add images' : 'Add images'}
                        >
                          <Icon name="Image" className="icon--sm" />
                          Images
                        </button>
                      </div>

                      <div className="mobile-action-stack">
                        <button
                          type="submit"
                          className="btn btn--primary btn--md post-button"
                          disabled={currentLoading || !title.trim()}
                        >
                          {currentLoading ? 'Posting...' : 'Post'}
                        </button>
                        <button
                          type="button"
                          className="share-protocol-modal-mobile-cancel"
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProtocolModal;
